import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import clsx from "clsx";

import type { IAjax } from "@Components/Hooks/useAjax";
import useAjax from "@Components/Hooks/useAjax";

import type { IExpandableListItem } from "./ExpandableListItem";
import ExpandableListItem from "./ExpandableListItem";

import * as css from "./ExpandableList.module.css";

export enum ExpandableListAppearance {
    Standard,
    Blue,
}

export interface IExpandableList extends IAjax<IExpandableListItem[]> {
    expandedIndex?: number;
    preventOnItemClick?: boolean;
    className?: string;
    isStructuredData?: boolean;
}

interface IExpandableListState {
    expanded: number[];
    listElements: IExpandableListItem[] | undefined;
}

export const ExpandableList: FC<IExpandableList> = (props) => {
    const [state, setState] = useState<IExpandableListState>({
        expanded: props.expandedIndex != null && props.expandedIndex > -1 ? [props.expandedIndex] : [],
        listElements: props.initialData,
    });

    const { request, errorContainer } = useAjax<IExpandableListItem[]>({
        ...props,
        success: (result) => setState({ expanded: state.expanded, listElements: result }),
    });

    const expandableListRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        request();
        if (expandableListRef.current && props.isStructuredData) {
            expandableListRef.current.setAttribute("itemscope", "true");
            expandableListRef.current.setAttribute("itemType", "https://schema.org/FAQPage");
        }
    }, []);

    return (
        <div ref={expandableListRef} className={clsx(css.expandableListComponent, props.className)}>
            {!state.listElements || state.listElements.length === 0
                ? null
                : state.listElements.map((item, index) => (
                      <ExpandableListItem
                          {...item}
                          key={index}
                          preventOnItemClick={props.preventOnItemClick}
                          isExpanded={state.expanded.indexOf(index) >= 0}
                          isStructuredData={props.isStructuredData}
                      />
                  ))}
            {errorContainer}
        </div>
    );
};
